import React, { useState } from 'react';
import { MapPinIcon } from 'lucide-react';
import { usePower } from '../../contexts/PowerContext';
import { useAccount } from '../../contexts/AccountContext';
import { PageHeader } from '../../components/ui/PageHeader';
import { BeforeYouGo } from '../../components/power/BeforeYouGo';
import { PowerOverview } from '../../components/power/PowerOverview';
import { PowerMap } from '../../components/power/PowerMap';
import { HowItWorks, PowerNearby, PowerReliabilityPanel, QuickActions, RecentReports, YourImpact } from '../../components/power/PowerPanels';
import { ReportPowerSheet } from '../../components/power/ReportPowerSheet';
import { powerIcons } from '../../components/power/PowerPrimitives';

/**
 * Campuzon Power.
 *
 * The hierarchy is deliberate: Before you go answers the question the person came
 * with, the overview and quick actions sit beside it, and everything below is
 * supporting evidence. Nothing on this page is equal in weight to the top card.
 */
export function Power() {
  const {
    campusLocationId,
    next,
    offline,
    setOffline,
    subscribed,
    toggleSubscription
  } = usePower();
  const {
    campusName
  } = useAccount();
  const [reporting, setReporting] = useState(false);
  const [reportLocation, setReportLocation] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(next?.locationId ?? null);
  const openReport = (locationId?: string) => {
    setReportLocation(locationId ?? selected ?? campusLocationId);
    setReporting(true);
  };
  const Zap = powerIcons.power;
  return <div className="space-y-5" data-id="element-11710" data-mp="38t4yei4vr3m">
      <PageHeader eyebrow="Campuzon Power" title="Power" description="Know where the light is before you move." documentTitle actions={<span className="inline-flex min-h-touch items-center gap-2 rounded-xl border border-line bg-surface px-3.5 text-body-sm font-medium text-ink" data-id="element-11712" data-mp="2ixmhgie4vyt0">
            <MapPinIcon className="h-4 w-4 text-ink-muted" aria-hidden data-id="element-11713" data-mp="2e02r4k45vvkp" />
            {campusName}
          </span>} data-id="element-11711" data-mp="h6rg7b15r7ew" />

      {/* Before you go leads, with the overview and quick actions beside it on
          wide screens and beneath it on narrow ones. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.05fr)_minmax(0,1.25fr)_minmax(0,0.8fr)]" data-id="element-11714" data-mp="rtb52zlylhl9">
        <BeforeYouGo data-id="element-11715" data-mp="vvehprkd6s84" />
        <PowerOverview onReport={openReport} data-id="element-11716" data-mp="vvxocc4kdu6u" />
        <QuickActions onReport={() => openReport()} onNotify={() => toggleSubscription(campusLocationId)} notifying={subscribed(campusLocationId)} data-id="element-11717" data-mp="10u8xekn1c0o0" />
      </div>

      {/* Deliberate reordering rather than a natural collapse: on a phone the
          glanceable nearby list is worth more than a map you have to pan, so the
          map drops below it and returns to the lead column on wide screens. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,1fr)]" data-id="element-11718" data-mp="1jls00qd5dcpi">
        <div className="order-2 min-w-0 xl:order-1" data-id="element-11719" data-mp="1okv86jofbz1p">
          <PowerMap selectedId={selected} onSelect={setSelected} data-id="element-11720" data-mp="1ohh1x0cn35oz" />
        </div>
        <div className="order-1 min-w-0 xl:order-2" data-id="element-11721" data-mp="3935mnyyz50x2">
          <PowerNearby data-id="element-11722" data-mp="342hzjodv9484" />
        </div>
        <div className="order-3 min-w-0" data-id="element-11723" data-mp="341gbmjbr4n8z">
          <RecentReports data-id="element-11724" data-mp="300wdpdjctfls" />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3" data-id="element-11725" data-mp="301pn6m9p6j7m">
        <PowerReliabilityPanel locationId={selected ?? campusLocationId} data-id="element-11726" data-mp="2v2mkh8h6wp2t" />
        <HowItWorks data-id="element-11727" data-mp="2v4ft2tbd5jdg" />
        <YourImpact onReport={() => openReport()} data-id="element-11728" data-mp="3rsvj0qidxur3" />
      </div>

      {/* Connectivity is a real state in this product, so it is reachable rather
          than theoretical: cached power data must never read as live. */}
      <p className="flex flex-wrap items-center gap-2 text-caption text-ink-subtle" data-id="element-11729" data-mp="3iz0dhhdcq2ax">
        <Zap className="h-3.5 w-3.5" aria-hidden data-id="element-11730" data-mp="3e1gwwat6ldft" />
        Power information is community-reported and combined with campus and property
        sources. It is guidance, not a guarantee.
        <button type="button" onClick={() => setOffline(!offline)} className="min-h-touch rounded-lg border border-line px-3 text-caption font-medium text-ink-muted transition-colors duration-150 ease-out hover:text-ink" data-id="element-11731" data-mp="akiahu29pcy6">
          {offline ? 'Reconnect' : 'Simulate losing connection'}
        </button>
      </p>

      <ReportPowerSheet open={reporting} onClose={() => setReporting(false)} presetLocationId={reportLocation} data-id="element-11732" data-mp="3hb9uccekyeos" />
    </div>;
}